import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {Blockquote, Modal} from "@wfp/ui";
import {KVPair, ListPage, Loss, ServerError, Sortable} from "../../models/types";
import {getLosses} from "../../restapi/loss";
import {updateSorting} from "../../restapi/restapi";
import {LossFilters} from "./filters";
import {LossTable} from "./table";
import {useDeleteLoss} from "../../hooks/loss";

const SortInfo: Sortable[] = [
    {code: 'created_on', field: 'created_on', enabled: true, mode: 0},
    {code: 'school', field: 'school_name', enabled: true, mode: 0},
    {code: 'partner', field: 'partner_name', enabled: true, mode: 0},
    {code: 'order_number', field: 'order_number', enabled: true, mode: 0},
    {code: 'item', field: 'content_name', enabled: true, mode: 0},
    {code: 'category', field: 'category_name', enabled: true, mode: 0},
    {code: 'quantity', field: 'quantity', enabled: true, mode: 0},
    {code: 'author', field: 'author_name', enabled: true, mode: 0},
];

export const LossList = () => {
    const {t} = useTranslation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<ServerError>();
    const [page, setPage] = useState<ListPage<Loss>>();
    const [confirmDelete, setConfirmDelete] = useState<Loss | null>(null);

    const deleteLossMutation = useDeleteLoss();

    // Derive sortInfo with current mode from page.sorting
    const currentSortInfo = SortInfo.map(sortable => {
        if (!page?.sorting) return sortable;

        const field = sortable.field;
        const sortEntry = page.sorting.find(s =>
            s === field || s === `+${field}` || s === `-${field}`
        );

        if (!sortEntry) return { ...sortable, mode: 0 };  // No sort
        if (sortEntry.startsWith('-')) return { ...sortable, mode: 2 };  // DESC
        if (sortEntry === field || sortEntry.startsWith('+')) return { ...sortable, mode: 1 };  // ASC
        return sortable;
    });

    useEffect(() => {
        loadPage(1);
    }, []);

    function loadPage(pageNumber = 1, pageSize?: number, options?: KVPair[], sorting?: string[]): void {
        setLoading(true);
        setError(undefined);
        getLosses(pageNumber, pageSize, options, sorting)
            .then((res) => {
                setPage(res);
            })
            .catch(error => setError(error))
            .finally(() => setLoading(false));
    }

    function handleSorting(field: string): void {
        const value = updateSorting(field, page?.sorting || []);
        loadPage(page?.pageNumber, page?.pageSize, page?.filtering, value);
    }

    function handleFiltering(filters: KVPair[]): void {
        const changed = filters.length !== page?.filtering?.length ||
            filters.filter(kvp => !page?.filtering?.find(f => f.key === kvp.key && f.value === kvp.value)).length > 0;
        if (changed) {
            loadPage(page?.pageNumber || 1, page?.pageSize, filters, page?.sorting);
        }
    }

    function handleDeleteSingle(loss: Loss) {
        setConfirmDelete(loss);
    }

    function confirmDeleteSingle() {
        if (confirmDelete) {
            deleteLossMutation.mutate(confirmDelete, {
                onSuccess: () => {
                    setConfirmDelete(null);
                    // Reload the current page
                    loadPage(page?.pageNumber, page?.pageSize, page?.filtering, page?.sorting);
                },
                onError: (error: any) => {
                    setError(error);
                    setConfirmDelete(null);
                }
            });
        }
    }

    return (
        <div>
            {!!error?.detail && (
                <Blockquote kind="error" withIcon>
                    {error.detail.toString()}
                </Blockquote>
            )}
            {!!page && (
                <div className="mx-0">
                    {page?.count === 0 && (
                        <Blockquote kind="warning" withIcon>
                            {t("empty_list")}
                        </Blockquote>
                    )}
                    {!!page && (
                        <>
                            <LossFilters onSubmit={handleFiltering}/>
                            <LossTable
                                page={page}
                                loadPage={loadPage}
                                hidePagination={page.count <= page.results.length}
                                sort={handleSorting}
                                sortInfo={currentSortInfo}
                                onDeleteSingle={handleDeleteSingle}
                            />
                        </>
                    )}
                </div>
            )}

            {confirmDelete && (
                <Modal
                    open={true}
                    primaryButtonText={t('discard') as string}
                    onRequestSubmit={confirmDeleteSingle}
                    secondaryButtonText={t('cancel') as string}
                    onSecondarySubmit={() => setConfirmDelete(null)}
                    onRequestClose={() => setConfirmDelete(null)}
                    danger
                >
                    <p>{t('confirm_discard_loss_message', {
                        item: confirmDelete.contentName,
                        quantity: confirmDelete.quantity,
                        school: confirmDelete.schoolName
                    })}</p>
                </Modal>
            )}
        </div>
    );
}
